import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu, X, ChevronDown, ArrowRight, ArrowUpRight, Sparkles, Phone, Mail,
  FileSignature, Plane, MapPin, UserCheck, Users, Network, ClipboardList,
  Banknote, Briefcase, Dumbbell, Stethoscope, HeartPulse,
  Microscope, Video, Crown, ShieldCheck, ExternalLink, Layers3, CalendarDays, Compass, Workflow,
} from "lucide-react";
import TritalLogo from "../TritalLogo";
import AnnouncementStrip from "./AnnouncementStrip";
import { useAuth } from "../../context/AuthContext";

/* ------------------------------------------------------------------ */
/*  Data contracts                                                     */
/* ------------------------------------------------------------------ */

const PHONE_DISPLAY = "+1 (832) 303-6622";
const PHONE_DIAL    = "+18323036622";
const EMAIL         = "info@tritalcare.com";
const SHIFTS_URL    = "https://shifts.tritalcare.com/";

/* Primary nav (center of main bar) */
const PRIMARY_NAV = [
  { to: "/",                          label: "Home" },
  { key: "employers",                 label: "Employers",  mega: true,  to: "/employers" },
  { to: "/clinicians",                label: "Clinicians" },
  { key: "services",                  label: "Services",   mega: true,  to: "/services" },
  { key: "solutions",                 label: "Solutions",  mega: true,  to: "/employers" },
  { key: "compliance",                label: "Compliance", mega: true,  to: "/compliance" },
  { to: "/compliance/checklists",     label: "Checklists" },
  { key: "platforms",              label: "Platforms", mega: true, to: "/platforms" },
  { to: "/diversity-and-inclusion", label: "Diversity" },
];

/* Utility links (utility bar) */
const UTILITY_NAV = [
  { to: "/about",   label: "About" },
  { to: "/careers", label: "Careers" },
  { to: "/hot-jobs", label: "Hot Jobs" },
  { to: "/contact", label: "Contact" },
];

/* ---------- Mega-menu content ---------- */

const EMPLOYERS_MEGA = {
  eyebrow: "For Health Systems & MSPs",
  title: "Your entire workforce, on one system of record.",
  description: "Travel, PRN, local, contract and permanent — orchestrated against your facility compliance rules.",
  cta: { label: "Request Talent", to: "/register?role=facility", Icon: ArrowRight },
  accent: "bg-trital-navy text-white",
  href: "/employers",
  links: [
    { Icon: FileSignature,  title: "Contract Staffing",     desc: "13–26 week clinical contracts",      to: "/employers#contract-staffing" },
    { Icon: UserCheck,      title: "Permanent Staffing",    desc: "Full-time clinical placements",      to: "/employers#permanent-staffing" },
    { Icon: Users,          title: "Direct Hire",            desc: "Permanent clinical recruiting",      to: "/services/contract-direct-hire" },
    { Icon: Plane,          title: "Travel Nurse Staffing",  desc: "National travel deployment",         to: "/services/travel-nursing" },
    { Icon: HeartPulse,     title: "Locum Support",          desc: "Physician + APP locum tenens",       to: "/services/physician-app-staffing" },
    { Icon: Banknote,       title: "Payroll Services",       desc: "Compliant multi-state payrolling",   to: "/employers#payroll" },
    { Icon: ClipboardList,  title: "SOW Support",            desc: "Scoped clinical engagements",        to: "/employers#sow" },
    { Icon: Network,        title: "EOR / AOR",              desc: "Employer & agent of record",         to: "/employers#eor-aor" },
  ],
};

const SERVICES_MEGA = {
  eyebrow: "Capabilities",
  title: "Every clinical discipline. One workforce system.",
  description: "Marketplace, credentialing, skills verification, VMS and compliant payrolling — one platform.",
  cta: { label: "Explore Services", to: "/services", Icon: ArrowRight },
  accent: "bg-slate-900 text-white",
  href: "/services",
  links: [
    { Icon: Plane,         title: "Travel Nursing",            desc: "13-week+ RN contracts nationwide",    to: "/services/travel-nursing" },
    { Icon: MapPin,        title: "Local & Per Diem Staffing", desc: "Same-week local & PRN coverage",      to: "/services/local-per-diem-staffing" },
    { Icon: Microscope,    title: "Allied Health Staffing",    desc: "Lab, imaging, respiratory",            to: "/services/allied-health-staffing" },
    { Icon: Dumbbell,      title: "Therapy Staffing",           desc: "PT, OT, SLP placements",               to: "/services/therapy-staffing" },
    { Icon: Stethoscope,   title: "Physician & APP Staffing",   desc: "MD, DO, NP, PA roles",                 to: "/services/physician-app-staffing" },
    { Icon: Crown,         title: "Executive Leadership",       desc: "Interim CNO, CMO, directors",          to: "/services/executive-leadership" },
    { Icon: Video,         title: "Telehealth Staffing",        desc: "Virtual-care clinicians",              to: "/services/telehealth-staffing" },
    { Icon: Briefcase,     title: "Contract & Direct Hire",     desc: "Temp-to-perm and direct hire",         to: "/services/contract-direct-hire" },
  ],
};

const SOLUTIONS_MEGA = {
  eyebrow: "Workforce Solutions",
  title: "Solutions built around your facility.",
  description: "Explore the original staffing, direct-hire, payroll and employer-of-record services. These remain part of TRITAL Care.",
  cta: { label: "View Employer Solutions", to: "/employers", Icon: ArrowRight },
  accent: "bg-trital-navy text-white",
  href: "/employers",
  links: [
    { Icon: FileSignature, title: "Contract Staffing", desc: "Travel and contract workforce solutions", to: "/employers#contract-staffing" },
    { Icon: UserCheck, title: "Permanent Staffing", desc: "Direct hiring for clinical teams", to: "/employers#permanent-staffing" },
    { Icon: MapPin, title: "Local & PRN Coverage", desc: "Flexible local and per-diem staffing", to: "/services/local-per-diem-staffing" },
    { Icon: Banknote, title: "Payroll Services", desc: "Multi-state workforce payrolling", to: "/employers#payroll" },
    { Icon: ClipboardList, title: "SOW Solutions", desc: "Defined clinical projects and deliverables", to: "/employers#sow" },
    { Icon: Network, title: "EOR / AOR", desc: "Employer and agent of record", to: "/employers#eor-aor" },
  ],
};

const COMPLIANCE_MEGA = {
  eyebrow: "Trust & Verification",
  title: "Audit-ready clinical workforce.",
  description: "Joint Commission-aligned credentialing, multi-state licensure tracking, competency scoring.",
  cta: { label: "Download audit pack", to: "/compliance", Icon: ArrowRight },
  accent: "bg-trital-red text-white",
  href: "/compliance",
  links: [
    { Icon: ShieldCheck,  title: "Compliance Overview",  desc: "Our full compliance framework",      to: "/compliance" },
    { Icon: ClipboardList, title: "All Skill Checklists", desc: "Explore every specialty competency checklist", to: "/compliance/checklists" },
    { Icon: HeartPulse,   title: "Nurse Checklist",      desc: "RN specialty competency checklists", to: "/compliance/checklists/nurse" },
    { Icon: Dumbbell,     title: "Therapist Checklist",  desc: "PT / OT / SLP competency",           to: "/compliance/checklists/therapist" },
    { Icon: Microscope,   title: "Other Checklist",      desc: "Allied, imaging, respiratory",       to: "/compliance/checklists/other" },
  ],
};

const PLATFORMS_MEGA = {
  eyebrow: "TRITAL Care Digital Ecosystem",
  title: "One connected workforce. Multiple specialized platforms.",
  description: "Explore ShiftBridge, Workforce OS, Orbit and Nexus. Each specialized platform is available from the TRITAL Care ecosystem.",
  cta: { label: "Explore All Platforms", to: "/platforms", Icon: ArrowRight },
  accent: "bg-trital-navy text-white",
  href: "/platforms",
  links: [
    { Icon: CalendarDays, title: "TRITAL ShiftBridge™", desc: "Browse PRN shifts & clinician onboarding", to: "https://shifts.tritalcare.com/" },
    { Icon: Compass, title: "TRITAL Orbit™", desc: "Open the Orbit workforce mobility app", to: "https://orbit.tritalcare.com/" },
    { Icon: Workflow, title: "TRITAL Nexus™", desc: "Open the Nexus recruiter operating system", to: "https://nexus.tritalcare.com/" },
    { Icon: Layers3, title: "TRITAL Workforce OS™", desc: "RateLab & recruiter intelligence tools", to: "/workforce-os" },
  ],
};
const MEGA_MAP = {
  employers: EMPLOYERS_MEGA,
  services: SERVICES_MEGA,
  solutions: SOLUTIONS_MEGA,
  compliance: COMPLIANCE_MEGA,
  platforms: PLATFORMS_MEGA,
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function PublicNav() {
  const { user } = useAuth();
  const [activeMega, setActiveMega] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();

  /* Close on route change */
  useEffect(() => {
    setActiveMega(null);
    setMobileOpen(false);
    setMobileSection(null);
  }, [location.pathname]);

  /* Escape to close */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { setActiveMega(null); setMobileOpen(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* Shrink state on scroll */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 64);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Body scroll lock when mobile open */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const openMega = (key) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setActiveMega(key);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setActiveMega(null), 180);
  };

  return (
    <>
      {/* =================== Announcement strip (dismissable) =================== */}
      <AnnouncementStrip />

      {/* =================== Utility bar =================== */}
      <div
        className="hidden md:block bg-trital-navy-ink text-white/75 border-b border-white/8"
        data-testid="utility-bar"
      >
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 h-9 flex items-center justify-between text-[12px] tracking-tight">
          <div className="flex items-center gap-5">
            <a
              href={`tel:${PHONE_DIAL}`}
              className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
              aria-label={`Call TRITAL Care at ${PHONE_DISPLAY}`}
              data-testid="utility-phone"
            >
              <Phone className="w-3 h-3 text-trital-red" strokeWidth={1.8} />
              <span className="tabular-nums">{PHONE_DISPLAY}</span>
            </a>
            <span className="text-white/20">·</span>
            <a
              href={`mailto:${EMAIL}`}
              className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
              data-testid="utility-email"
            >
              <Mail className="w-3 h-3 text-trital-red" strokeWidth={1.8} />
              {EMAIL}
            </a>
          </div>

          <div className="flex items-center gap-5">
            {UTILITY_NAV.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `hover:text-white transition-colors ${isActive ? "text-white" : ""}`
                }
                data-testid={`utility-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
            <span className="text-white/20">·</span>
            {user ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-1 hover:text-white font-semibold"
                data-testid="utility-dashboard"
              >
                Dashboard <ArrowRight className="w-3 h-3" strokeWidth={1.8} />
              </Link>
            ) : (
              <Link
                to="/login"
                className="hover:text-white font-semibold"
                data-testid="utility-signin"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* =================== Main bar =================== */}
      <header
        className={`sticky top-0 z-50 bg-white/92 backdrop-blur-xl border-b border-slate-200 transition-[height,box-shadow] duration-200 ${
          scrolled ? "shadow-[0_4px_14px_-4px_rgba(15,23,42,0.08)]" : ""
        }`}
        data-testid="public-nav"
        onMouseLeave={scheduleClose}
      >
        <div
          className={`max-w-[1600px] mx-auto px-3 sm:px-4 2xl:px-8 flex items-center gap-1.5 xl:gap-2 2xl:gap-4 min-w-0 transition-[height] duration-200 ${
            scrolled ? "h-[68px]" : "h-[84px]"
          }`}
        >
          {/* Brand */}
          <Link to="/" className="shrink-0" data-testid="nav-logo-link" aria-label="TRITAL Care home">
            <TritalLogo size="sm" className={scrolled ? "" : "min-[1536px]:[&_img]:!h-14"} />
          </Link>

          {/* Desktop primary nav */}
          <nav
            className="hidden min-[1100px]:flex min-w-0 flex-1 items-center justify-between gap-0 2xl:gap-0.5 ml-1"
            aria-label="Primary"
          >
            {PRIMARY_NAV.map((item) => {
              if (item.mega) {
                return (
                  <button
                    key={item.key}
                    type="button"
                    onMouseEnter={() => openMega(item.key)}
                    onFocus={() => openMega(item.key)}
                    onClick={() => setActiveMega(activeMega === item.key ? null : item.key)}
                    className={`${item.key === "solutions" ? "hidden min-[1536px]:inline-flex" : "inline-flex"} relative px-1.5 xl:px-2 2xl:px-3.5 py-2 rounded-full text-[11.5px] xl:text-[12px] 2xl:text-[13.5px] font-semibold tracking-tight whitespace-nowrap items-center gap-0.5 2xl:gap-1 transition-colors ${
                      activeMega === item.key
                        ? "bg-slate-100 text-trital-navy"
                        : "text-slate-700 hover:text-trital-navy"
                    }`}
                    data-testid={`mega-trigger-${item.key}`}
                    aria-haspopup="true"
                    aria-expanded={activeMega === item.key}
                    aria-controls={`mega-panel-${item.key}`}
                  >
                    {item.label}
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${activeMega === item.key ? "rotate-180" : ""}`}
                      strokeWidth={1.8}
                    />
                  </button>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onMouseEnter={() => setActiveMega(null)}
                  className={({ isActive }) =>
                    `${item.to === "/compliance/checklists" ? "hidden min-[1350px]:inline-flex" : "inline-flex"} px-1.5 xl:px-2 2xl:px-3.5 py-2 rounded-full text-[11.5px] xl:text-[12px] 2xl:text-[13.5px] font-semibold tracking-tight whitespace-nowrap transition-colors ${
                      isActive ? "text-trital-navy bg-slate-100" : "text-slate-700 hover:text-trital-navy"
                    }`
                  }
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          {/* Right CTA cluster */}
          <div className="hidden min-[1100px]:flex items-center gap-1 2xl:gap-2 shrink-0 ml-1">
            <Link
              to="/register?role=facility"
              className="inline-flex items-center justify-center text-center leading-[1.15] max-w-[82px] 2xl:max-w-none px-2.5 2xl:px-4 py-2.5 rounded-full bg-trital-navy text-white text-[11.5px] 2xl:text-[13.5px] font-semibold btn-lift hover:bg-trital-navy-deep shadow-[0_4px_14px_-4px_rgba(19,39,122,0.4)]"
              data-testid="nav-request-talent"
            >
              Request Talent
            </Link>
            <Link
              to="/register?role=clinician"
              className="group inline-flex items-center justify-center text-center leading-[1.15] gap-1 max-w-[125px] 2xl:max-w-none px-2.5 2xl:px-4 py-2.5 rounded-full bg-trital-red text-white text-[11.5px] 2xl:text-[13.5px] font-semibold btn-lift hover:bg-trital-red-deep"
              data-testid="nav-find-opportunities"
            >
              Find Opportunities
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.8} />
            </Link>
            <a
                href={SHIFTS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center text-center leading-[1.15] gap-1 max-w-[83px] 2xl:max-w-none px-2.5 2xl:px-4 py-2.5 rounded-full bg-white border border-slate-300 text-trital-navy text-[11.5px] 2xl:text-[13.5px] font-semibold btn-lift hover:border-trital-navy hover:bg-slate-50"
              aria-label="PRN Shifts (opens in new tab)"
              data-testid="nav-prn-shifts"
            >
              PRN Shifts
              <ExternalLink className="w-3.5 h-3.5 opacity-60" strokeWidth={1.8} />
              </a>
            
          </div>

          {/* Tablet actions; full desktop menu starts only when it can fit. */}
          <div className="hidden sm:flex min-[1100px]:hidden items-center gap-1.5 ml-auto shrink-0">
            <Link to="/register?role=facility" className="hidden min-[850px]:inline-flex items-center justify-center rounded-full px-3 py-2.5 bg-trital-navy text-white text-[12px] font-semibold whitespace-nowrap" data-testid="tablet-request-talent">Request Talent</Link>
            <Link to="/register?role=clinician" className="hidden min-[640px]:inline-flex items-center justify-center rounded-full px-3 py-2.5 bg-trital-red text-white text-[12px] font-semibold whitespace-nowrap" data-testid="tablet-find-opportunities">Find Opportunities</Link>
            <a href={SHIFTS_URL} target="_blank" rel="noopener noreferrer" aria-label="PRN Shifts (opens in new tab)" className="inline-flex items-center justify-center rounded-full px-3 py-2.5 border border-slate-300 text-trital-navy text-[12px] font-semibold whitespace-nowrap" data-testid="tablet-prn-shifts">PRN Shifts</a>
          </div>
          {/* PRN link remains accessible on narrow phones, alongside the menu. */}
          <a href={SHIFTS_URL} target="_blank" rel="noopener noreferrer" className="sm:hidden ml-auto rounded-full border border-slate-300 px-3 py-2 text-[12px] font-semibold text-trital-navy" data-testid="phone-prn-shifts">PRN</a>

          {/* Mobile / compact desktop trigger */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="min-[1100px]:hidden p-2 shrink-0 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" strokeWidth={1.8} /> : <Menu className="w-5 h-5" strokeWidth={1.8} />}
          </button>
        </div>

        {/* =================== Mega-menu panel =================== */}
        <AnimatePresence>
          {activeMega && (
            <motion.div
              key={activeMega}
              id={`mega-panel-${activeMega}`}
              role="region"
              aria-label={`${activeMega} menu`}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              onMouseEnter={() => openMega(activeMega)}
              onMouseLeave={scheduleClose}
              className="absolute left-0 right-0 top-full bg-white border-b border-slate-200 shadow-[0_30px_60px_-20px_rgba(15,23,42,0.15)]"
              data-testid={`mega-panel-${activeMega}`}
            >
              <MegaContent mega={MEGA_MAP[activeMega]} onNavigate={() => setActiveMega(null)} />
            </motion.div>
          )}
        </AnimatePresence>
      
      {/* =================== Mobile drawer =================== */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="min-[1100px]:hidden absolute inset-x-0 top-full z-40 bg-white overflow-y-auto border-t border-slate-200 shadow-xl"
            style={{ maxHeight: scrolled ? "calc(100dvh - 68px)" : "calc(100dvh - 84px)" }}
            data-testid="mobile-drawer"
            role="dialog"
            aria-label="Mobile navigation"
          >
            <div className="px-5 py-6 pb-28 space-y-1">
              {/* Primary nav items */}
              {PRIMARY_NAV.map((item) => {
                if (item.mega) {
                  return (
                    <MobileAccordion
                      key={item.key}
                      label={item.label}
                      mega={MEGA_MAP[item.key]}
                      open={mobileSection === item.key}
                      onToggle={() => setMobileSection(mobileSection === item.key ? null : item.key)}
                      onNavigate={() => setMobileOpen(false)}
                    />
                  );
                }
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className="block px-4 py-4 rounded-xl font-display font-bold text-lg text-slate-900 hover:bg-slate-50"
                    data-testid={`mobile-link-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                );
              })}

              {/* Divider */}
              <div className="h-px bg-slate-200 my-4" />

              {/* Utility nav */}
              {UTILITY_NAV.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-3 rounded-xl text-[15px] font-semibold text-slate-600 hover:bg-slate-50"
                  data-testid={`mobile-utility-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </Link>
              ))}

              {/* Contact block */}
              <div className="mt-4 px-4 py-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                <a
                  href={`tel:${PHONE_DIAL}`}
                  className="flex items-center gap-2 text-sm text-slate-700"
                  data-testid="mobile-phone"
                >
                  <Phone className="w-4 h-4 text-trital-red" strokeWidth={1.8} />
                  {PHONE_DISPLAY}
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  className="flex items-center gap-2 text-sm text-slate-700"
                  data-testid="mobile-email"
                >
                  <Mail className="w-4 h-4 text-trital-red" strokeWidth={1.8} />
                  {EMAIL}
                </a>
              </div>

              {/* CTA cluster */}
              <div className="pt-5 mt-5 border-t border-slate-200 space-y-2.5">
                <Link
                  to="/register?role=facility"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-navy text-white font-semibold"
                  data-testid="mobile-request-talent"
                >
                  Request Talent
                </Link>
                <Link
                  to="/register?role=clinician"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-red text-white font-semibold"
                  data-testid="mobile-find-opportunities"
                >
                  Find Opportunities <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                </Link>
                <a
                  href={SHIFTS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-full border border-slate-300 text-trital-navy font-semibold"
                  aria-label="PRN Shifts (opens in new tab)"
                  data-testid="mobile-prn-shifts"
                >
                  PRN Shifts <ExternalLink className="w-4 h-4" strokeWidth={1.8} />
                </a>
                
                {user ? (
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileOpen(false)}
                    className="block text-center px-5 py-3.5 rounded-full border border-slate-300 text-slate-700 font-semibold"
                    data-testid="mobile-dashboard"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    className="block text-center px-5 py-3.5 text-[14px] text-slate-600"
                    data-testid="mobile-signin"
                  >
                    Sign in
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </header>

      {/* =================== Sticky mobile CTA strip =================== */}
      <MobileStickyCtaBar scrolled={scrolled} drawerOpen={mobileOpen} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Mega panel content                                                 */
/* ------------------------------------------------------------------ */

function MegaContent({ mega, onNavigate }) {
  const singleCol = mega.links.length <= 4;
  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-[1.05fr_2fr] gap-10">
      {/* Feature card */}
      <motion.div
        initial={{ opacity: 0, x: -12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.04, duration: 0.25 }}
        className={`relative overflow-hidden rounded-3xl p-8 ${mega.accent} min-h-[260px] flex flex-col justify-between`}
      >
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-20 -left-10 w-56 h-56 rounded-full bg-white/5 blur-3xl" aria-hidden="true" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold opacity-80">
            <Sparkles className="w-3 h-3" strokeWidth={1.8} /> {mega.eyebrow}
          </div>
          <div className="mt-4 font-display font-black text-3xl lg:text-[32px] leading-[1.08] tracking-tight text-balance">
            {mega.title}
          </div>
          <p className="mt-3 text-sm opacity-85 leading-relaxed max-w-[34ch]">{mega.description}</p>
        </div>
        <div className="relative mt-6 flex items-center gap-3">
          <Link
            to={mega.cta.to}
            onClick={onNavigate}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-slate-900 text-sm font-semibold btn-lift hover:bg-slate-100"
            data-testid={`mega-cta-${mega.cta.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {mega.cta.label}
            <mega.cta.Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
          </Link>
          <Link
            to={mega.href}
            onClick={onNavigate}
            className="text-sm text-white/80 hover:text-white inline-flex items-center gap-1"
          >
            Learn more <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.8} />
          </Link>
        </div>
      </motion.div>

      {/* Link grid */}
      <div className={`grid ${singleCol ? "sm:grid-cols-1" : "sm:grid-cols-2"} gap-x-8 gap-y-1`}>
        {mega.links.map((item, idx) => (
          <MegaLink key={item.title} item={item} idx={idx} onNavigate={onNavigate} />
        ))}
      </div>
    </div>
  );
}

function MegaLink({ item, idx, onNavigate }) {
  const isExternal = /^https?:\/\//i.test(item.to || "");
  const isHash = !isExternal && item.to && item.to.includes("#");
  const LinkComp = isExternal || isHash ? "a" : Link;
  const linkProp = isExternal ? { href: item.to, target: "_blank", rel: "noopener noreferrer" }
    : isHash ? { href: item.to } : { to: item.to };
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 + idx * 0.025, duration: 0.22 }}
    >
      <LinkComp
        {...linkProp}
        onClick={onNavigate}
        className="group flex items-start gap-4 px-4 py-4 rounded-xl hover:bg-slate-50 transition-colors"
        data-testid={`mega-link-${item.title.toLowerCase().replace(/\s+|\//g, "-").replace(/&/g, "and")}`}
      >
        <div className="shrink-0 w-11 h-11 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center group-hover:bg-trital-navy group-hover:text-white transition-colors">
          <item.Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-[15px] text-slate-900 group-hover:text-trital-navy flex items-center gap-1">
            {item.title}
            <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-trital-red" strokeWidth={1.8} />
          </div>
          <div className="mt-0.5 text-[12.5px] text-slate-500 leading-snug">{item.desc}</div>
        </div>
      </LinkComp>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Mobile accordion                                                   */
/* ------------------------------------------------------------------ */

function MobileAccordion({ label, mega, open, onToggle, onNavigate }) {
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-4 font-display font-bold text-lg text-slate-900"
        aria-expanded={open}
        data-testid={`mobile-section-${label.toLowerCase()}`}
      >
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.8} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pb-3 space-y-1">
              {mega.links.map((item) => {
                const isExternal = /^https?:\/\//i.test(item.to || "");
                const isHash = !isExternal && item.to && item.to.includes("#");
                const LinkComp = isExternal || isHash ? "a" : Link;
                const linkProp = isExternal ? { href: item.to, target: "_blank", rel: "noopener noreferrer" }
                  : isHash ? { href: item.to } : { to: item.to };
                return (
                  <LinkComp
                    key={item.title}
                    {...linkProp}
                    onClick={onNavigate}
                    className="flex items-start gap-3 px-4 py-3 rounded-xl hover:bg-slate-50"
                  >
                    <div className="w-9 h-9 rounded-lg bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center shrink-0">
                      <item.Icon className="w-4 h-4" strokeWidth={1.8} />
                    </div>
                    <div>
                      <div className="font-semibold text-[14px] text-slate-900">{item.title}</div>
                      <div className="text-[12px] text-slate-500">{item.desc}</div>
                    </div>
                  </LinkComp>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Mobile sticky CTA bar (<lg, after scroll)                          */
/* ------------------------------------------------------------------ */

function MobileStickyCtaBar({ scrolled, drawerOpen }) {
  if (drawerOpen) return null;
  return (
    <div
      className={`lg:hidden fixed bottom-0 inset-x-0 z-40 transition-transform duration-200 ${
        scrolled ? "translate-y-0" : "translate-y-full"
      }`}
      data-testid="mobile-sticky-cta"
      aria-hidden={!scrolled}
    >
      <div
        className="bg-white border-t border-slate-200 shadow-[0_-10px_30px_-10px_rgba(15,23,42,0.12)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="px-4 py-3 flex gap-2">
          <Link
            to="/register?role=facility"
            className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-full bg-trital-navy text-white text-sm font-semibold"
            data-testid="mobile-cta-request-talent"
          >
            Request Talent
          </Link>
          <Link
            to="/careers/jobs"
            className="flex-1 inline-flex items-center justify-center gap-1 px-4 py-3 rounded-full bg-trital-red text-white text-sm font-semibold"
            data-testid="mobile-cta-find-jobs"
          >
            Find Jobs <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
          </Link>
        </div>
      </div>
    </div>
  );
}
